<template>
  <section class="ws-step" aria-labelledby="ws-heading" data-testid="welcome-step">
    <OrganizationBrand :logo="logo" :account-name="accountName" />

    <div class="ws-heading">
      <h2 id="ws-heading" class="ws-title" data-testid="ws-title">Welcome, {{ firstName }}</h2>
    </div>

    <div class="ws-content">
      <p v-if="introText" class="ws-intro" data-testid="ws-intro">{{ introText }}</p>
      <p v-if="subText" class="ws-subtext" data-testid="ws-subtext">{{ subText }}</p>

      <ol v-if="pillars.length" class="ws-pillars" data-testid="ws-pillars">
        <li
          v-for="(pillar, index) in pillars"
          :key="index"
          class="ws-pillar"
          data-testid="ws-pillar"
        >
          <span class="ws-pillar-number" aria-hidden="true">{{ index + 1 }}</span>
          <span class="ws-pillar-text">{{ pillar }}</span>
        </li>
      </ol>
    </div>

    <div class="ws-spacer" aria-hidden="true"></div>

    <div class="ws-actions">
      <button
        type="button"
        class="ws-btn ws-btn--primary"
        data-testid="ws-get-started"
        @click="handleContinue"
      >
        {{ buttonLabel }}
      </button>
    </div>
  </section>
</template>

<script setup>
import OrganizationBrand from "@components/OrganizationBrand/OrganizationBrand.vue";

/**
 * ER-12245 — WelcomeStep (MDP-621).
 *
 * Step-1 content of every onboarding wizard. The heading is derived from the first
 * name; the welcome body, the readiness-pillar list and the button label are copy
 * supplied per flow (Employee / Admin / Admin + Employee). With no account logo the
 * OrganizationBrand renders nothing, so nothing appears above the title.
 * Presentational only: it emits `continue` and never navigates or mutates state.
 */
defineProps({
  /** Signed-in user's first name ("Welcome, {firstName}"). */
  firstName: {
    type: String,
    default: "",
  },
  /** Account name (alt text for the logo). */
  accountName: {
    type: String,
    default: "",
  },
  /** Account logo URL; empty hides the brand block. */
  logo: {
    type: String,
    default: "",
  },
  /** Welcome body paragraph. */
  introText: {
    type: String,
    default: "",
  },
  /** Optional second paragraph (readiness-badge intro). */
  subText: {
    type: String,
    default: "",
  },
  /** The numbered pillars (e.g. "Acquiring the necessary knowledge"). */
  pillars: {
    type: Array,
    default: () => [],
  },
  /** Primary action label. */
  buttonLabel: {
    type: String,
    default: "Get started",
  },
});

const emit = defineEmits(["continue"]);

function handleContinue() {
  emit("continue");
}
</script>

<style scoped>
.ws-step {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  height: 100%;
  padding: 0;
  background: #ffffff;
  color: #101828;
  font-family: Inter, sans-serif;
}

.ws-heading {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ws-title {
  margin: 0;
  color: #101828;
  font-size: 36px;
  font-weight: 600;
  line-height: 44px;
}

.ws-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ws-intro,
.ws-subtext {
  margin: 0;
  color: #475467;
  font-size: 16px;
  line-height: 24px;
}

.ws-pillars {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.ws-pillar {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ws-pillar-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  background: #7f56d9;
  border-radius: 50%;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.ws-pillar-text {
  color: #101828;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
}

.ws-spacer {
  flex: 1 1 auto;
}

.ws-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ws-btn {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 40px;
  min-width: 120px;
  padding: 0 16px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
}

.ws-btn--primary {
  background: #7f56d9;
  border: 1px solid #7f56d9;
  color: #ffffff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.ws-btn:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 1px;
}
</style>
